// @ts-check
/**
 * Ticker - Continuous scroll-based animation system
 * Reverse engineered from Loom Digital's implementation
 */

class Ticker {
    /**
     * @param {Object} [options={}]
     * @param {number} [options.breakpoint]
     * @param {number} [options.mobileSpeedMultiplier]
     * @param {boolean} [options.includeScrollVelocity]
     */
    constructor(options = {}) {
        /** @type {Array<{element: HTMLElement, top: number, bottom: number, speed: number, reverse: boolean, progress: number}>} */
        this.items = [];
        /** @type {number} */
        this.prevFrameScroll = 0;
        /** @type {number} */
        this.currentScroll = 0;
        /** @type {number|null} */
        this.animationFrameId = null;
        /** @type {boolean} */
        this.isScrolling = false;
        /** @type {ReturnType<typeof setTimeout>|undefined} */
        this.resizeTimeout = undefined;

        // Configuration
        this.config = {
            breakpoint: options.breakpoint || 1024,
            mobileSpeedMultiplier: options.mobileSpeedMultiplier || 1.5,
            includeScrollVelocity: options.includeScrollVelocity !== false,
            ...options
        };

        this.init();
    }

    /**
     * Get element's position relative to document
     * @param {HTMLElement} element
     * @returns {{top: number, bottom: number}}
     */
    getElementPosition(element) {
        const rect = element.getBoundingClientRect();
        return {
            top: rect.top + window.pageYOffset,
            bottom: rect.top + window.pageYOffset + element.offsetHeight
        };
    }

    /**
     * Check if element is in viewport
     * @param {number} top
     * @param {number} bottom
     * @returns {boolean}
     */
    isInViewport(top, bottom) {
        const scrollTop = window.pageYOffset;
        const windowHeight = window.innerHeight;
        const scrollBottom = scrollTop + windowHeight;

        return bottom >= scrollTop && top <= scrollBottom;
    }

    /**
     * Initialize ticker elements
     */
    init() {
        // Clear existing items
        this.items = [];
        this.prevFrameScroll = window.pageYOffset;

        // Find all elements with data-ticker
        const elements = document.querySelectorAll('[data-ticker]');

        if (!elements.length) return;

        elements.forEach(element => {
            const htmlElement = /** @type {HTMLElement} */ (element);
            const position = this.getElementPosition(htmlElement);

            // Parse configuration from data attribute
            /** @type {{speed?: number, reverse?: boolean}} */
            let config = {};
            try {
                const attrValue = htmlElement.getAttribute('data-ticker');
                if (attrValue) {
                    config = JSON.parse(attrValue);
                }
            } catch (e) {
                console.warn('Invalid ticker config:', e);
            }

            // Store item data
            this.items.push({
                element: htmlElement,
                top: position.top,
                bottom: position.bottom,
                speed: parseFloat(String(config.speed)) || 0.000001,
                reverse: config.reverse || false,
                progress: 0
            });
        });

        // Start animation loop if items exist
        if (this.items.length) {
            this.startAnimationLoop();
        }
    }

    /**
     * Update positions on resize
     */
    updatePositions() {
        this.items.forEach(item => {
            const position = this.getElementPosition(item.element);
            item.top = position.top;
            item.bottom = position.bottom;
        });
    }

    /**
     * Animation loop
     */
    animate() {
        this.currentScroll = window.pageYOffset;
        const windowWidth = window.innerWidth;
        const isMobile = windowWidth <= this.config.breakpoint;

        this.items.forEach(item => {
            // Only animate if element is in viewport
            if (!this.isInViewport(item.top, item.bottom)) {
                return;
            }

            // Calculate scroll velocity and direction
            let velocityBonus = 0;
            let isScrollingUp = false;
            const scrollDelta = this.currentScroll - this.prevFrameScroll;

            if (this.config.includeScrollVelocity && windowWidth > this.config.breakpoint) {
                const absScrollDelta = Math.abs(scrollDelta);
                velocityBonus = absScrollDelta / 40; // Reduced for slower scroll boost
                isScrollingUp = scrollDelta < -0.5; // Negative means scrolling up
            }

            // Adjust speed for mobile
            const speed = isMobile
                ? item.speed * this.config.mobileSpeedMultiplier
                : item.speed;

            // Get current progress
            let progress = parseFloat(
                getComputedStyle(item.element).getPropertyValue('--progress')
            ) || 0;

            // Determine direction based on scroll state
            let shouldReverse = item.reverse;

            // When scrolling up, reverse the direction
            if (isScrollingUp) {
                shouldReverse = !shouldReverse;
            }

            // Update progress based on direction
            if (shouldReverse) {
                // Reverse mode - move opposite direction
                progress -= speed + (velocityBonus * speed);
                if (progress < 0) progress = 1;
            } else {
                // Normal forward progression
                progress += speed + (velocityBonus * speed);
                if (progress > 1) progress = 0;
            }

            // Update progress value
            item.progress = progress;

            // Apply to element
            item.element.style.setProperty('--progress', String(progress));
        });

        this.prevFrameScroll = this.currentScroll;

        // Continue animation loop
        this.animationFrameId = requestAnimationFrame(() => this.animate());
    }

    /**
     * Start the animation loop
     */
    startAnimationLoop() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        this.animate();
    }

    /**
     * Stop the animation loop
     */
    stop() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    /**
     * Destroy instance and clean up
     */
    destroy() {
        this.stop();
        this.items = [];

        // Remove resize listener if it exists
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
    }

    /**
     * Handle window resize with debounce
     */
    handleResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            this.updatePositions();
        }, 500);
    }
}

// Auto-initialize if DOM is ready
if (typeof window !== 'undefined') {
    /** @type {Ticker|null} */
    let tickerInstance = null;

    const initTicker = () => {
        tickerInstance = new Ticker();

        // Handle resize
        window.addEventListener('resize', () => {
            if (tickerInstance) {
                tickerInstance.handleResize();
            }
        });
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initTicker);
    } else {
        initTicker();
    }

    // Export for manual control
    // @ts-ignore - Extending window object
    window.Ticker = Ticker;
    // @ts-ignore - Extending window object
    window.tickerInstance = tickerInstance;
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = Ticker;
}
